import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MyGrades, Standings } from '../../shared/api/types';
import { Alert, Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { shortDate } from './shared';

const MEDALS = ['🥇', '🥈', '🥉'];
/** 🥇🥈🥉 para los tres primeros lugares (con puntos); si no, el número. */
export const placeLabel = (position: number, points = 1) => (points > 0 && position <= 3 ? MEDALS[position - 1]! : `${position}.º`);

/** Tabla de posiciones del salón: suma de calificaciones, este mes o en total. */
export function StandingsTab({ base }: { base: string }) {
  const [period, setPeriod] = useState<'month' | 'total'>('month');
  const data = useAsync((signal) => api.get<Standings>(`${base}/standings?period=${period}`, signal), [base, period]);
  return (
    <Card title="Tabla de posiciones">
      <div className="segmented" role="radiogroup" aria-label="Periodo">
        {(
          [
            ['month', 'Este mes'],
            ['total', 'Total'],
          ] as const
        ).map(([k, label]) => (
          <label key={k} className="check">
            <input type="radio" checked={period === k} onChange={() => setPeriod(k)} /> {label}
          </label>
        ))}
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        data.data.entries.length === 0 ? (
          <p className="muted">El salón aún no tiene alumnos.</p>
        ) : (
          <>
            <p className="small muted">
              {data.data.kids ? '¡Contesta los cuestionarios y sube en la tabla! 🌟' : 'Cada cuestionario suma los puntos de tu calificación. ¡Participa y sube en la tabla!'}
            </p>
            <ol className={`standings${data.data.kids ? ' standings--kids' : ''}`}>
              {data.data.entries.map((e, i) => (
                <li key={i} className={`standings__row${e.me ? ' standings__row--me' : ''}${e.position <= 3 && e.points > 0 ? ' standings__row--top' : ''}`}>
                  <span className="standings__place">{placeLabel(e.position, e.points)}</span>
                  <span className="standings__name">
                    {e.name}
                    {e.me ? <span className="badge">Tú</span> : null}
                  </span>
                  <span className="standings__points">
                    <strong>{e.points}</strong> pts
                    <span className="small muted block">{e.answered === 1 ? '1 cuestionario' : `${e.answered} cuestionarios`}</span>
                  </span>
                </li>
              ))}
            </ol>
            <p className="small muted">
              Las preguntas abiertas suman cuando tu maestro las califica. Si prefieres que tu nombre no salga aquí, cámbialo en <Link to="/app/perfil">Mi perfil</Link>.
            </p>
          </>
        )
      ) : null}
    </Card>
  );
}

/** «Mis calificaciones» en Mi perfil: lo que contesté en Raíces, mi promedio y si mi nombre sale en las tablas. */
export function MyGradesCard({ churchId }: { churchId: string }) {
  const data = useAsync((signal) => api.get<MyGrades>(`/organization/churches/${churchId}/my-grades`, signal), [churchId]);
  const [error, setError] = useState<string | null>(null);
  if (data.status !== 'ready' || data.data.items.length === 0) return null;
  const g = data.data;

  async function toggle(hideName: boolean) {
    setError(null);
    try {
      await api.put('/organization/ranking-privacy', { hideName });
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Card title="Mis calificaciones">
      {g.average !== null ? (
        <p>
          Promedio: <strong>{g.average}%</strong>
        </p>
      ) : null}
      <ul className="list">
        {g.items.map((i) => (
          <li key={i.quizId} className="list__item list__item--actions">
            <Link className="list__link" to={i.session ? `/app/salones/${i.classroom.id}/clases/${i.session.id}` : `/app/salones/${i.classroom.id}?ver=temario`}>
              <strong>{i.title}</strong>
              <span className="muted small">
                {i.classroom.name}
                {i.session ? ` · ${i.session.topic ?? 'Clase'} (${shortDate(i.session.startsAt)})` : ''} · contestado {shortDate(i.submittedAt)}
              </span>
            </Link>
            <span>{i.maxScore ? `${i.score} / ${i.maxScore}` : 'Por calificar'}</span>
          </li>
        ))}
      </ul>
      {error ? <Alert>{error}</Alert> : null}
      <label className="check">
        <input type="checkbox" checked={g.hideName} onChange={(e) => void toggle(e.target.checked)} /> Ocultar mi nombre en las tablas de posiciones (saldré como «Participante»)
      </label>
    </Card>
  );
}
